import { injectable } from 'inversify';
import { FilterKey } from '@studyportals/search-filters';

import { FilterSelectionState } from '@/filters/selection/FilterSelectionState';
import { FilterOptions } from '@/filters/options/FilterOptions';
import { SortingOptions } from '@/sorting/SortingOptions';
import { SortingState } from '@/sorting/SortingState';

@injectable()
export class SeoInfoBase {
	public static readonly RANKING_YEAR_SWITCH = 9;

	protected readonly filterOptions: FilterOptions;

	public constructor(filterOptions: FilterOptions) {
		this.filterOptions = filterOptions;
	}

	protected selectionTypeCount(filterSelectionState: FilterSelectionState): number {
		return filterSelectionState.reactive.identifySelectedFilterKeys().length;
	}

	protected uniqueTypeSelectionFor(filterKey: FilterKey, filterSelectionState: FilterSelectionState): Promise<boolean> {
		return Promise.resolve(
			filterSelectionState.reactive.getFor(filterKey).length > 0 &&
				filterSelectionState.reactive.identifySelectedFilterKeys().length === 1
		);
	}

	protected selectionOnlyFor(keys: FilterKey[], filterSelectionState: FilterSelectionState): Promise<boolean> {
		const selected = filterSelectionState.reactive.identifySelectedFilterKeys();
		const allKeysIncluded = keys.every((key) => selected.includes(key));
		return Promise.resolve(selected.length === keys.length && allKeysIncluded);
	}

	protected selectionFor(filterKey: FilterKey, filterSelectionState: FilterSelectionState): Promise<boolean> {
		return Promise.resolve(filterSelectionState.reactive.getFor(filterKey).length > 0);
	}

	protected singleSelectionFor(filterKey: FilterKey, filterSelectionState: FilterSelectionState): Promise<boolean> {
		return Promise.resolve(filterSelectionState.reactive.getFor(filterKey).length === 1);
	}

	protected multipleSelectionFor(filterKey: FilterKey, filterSelectionState: FilterSelectionState): Promise<boolean> {
		return Promise.resolve(filterSelectionState.reactive.getFor(filterKey).length > 1);
	}

	protected getFilterOptionNameBy(filterKey: FilterKey, filterSelectionState: FilterSelectionState): string {
		const filterSelection = filterSelectionState.reactive.getFor(filterKey);

		return this.filterOptions.getForByValue(filterKey, filterSelection[0])?.optionName || '';
	}

	protected getFilterOptionValueBy(filterKey: FilterKey, filterSelectionState: FilterSelectionState): string | null {
		const filterSelection = filterSelectionState.reactive.getFor(filterKey);
		if (filterSelection.length === 0) {
			return null;
		}

		return filterSelection[0];
	}

	protected sortingOptionSelected(option: SortingOptions, sortState: SortingState): Promise<boolean> {
		return Promise.resolve(sortState.isSelected(option));
	}

	protected getCurrentYearForSeo(): number {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();

		if (currentDate.getMonth() >= SeoInfoBase.RANKING_YEAR_SWITCH) {
			return currentYear + 1;
		}

		return currentYear;
	}
}
